import { checkNever } from '@framework/engine/utils/checkNever.ts';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { type TagProps } from 'antd';
import dayjs from 'dayjs';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledTagWithMargin = styled(StyledTag)`
  margin-right: 0;
`;

enum CertificateValidity {
  Valid = 'Valid',
  Expired = 'Expired',
  NotYetValid = 'NotYetValid',
  Unknown = 'Unknown',
}

/**
 * Calcola la validità del certificato al momento della firma, in base al range
 * `validitaCertificatoDal` → `validitaCertificatoAl`.
 */
function getValidity(
  signatureDate: string | null | undefined,
  validFrom: string | null | undefined,
  validUntil: string | null | undefined,
): CertificateValidity {
  if (!(signatureDate && validFrom && validUntil))
    return CertificateValidity.Unknown;

  const d = dayjs(signatureDate);

  if (d.isBefore(dayjs(validFrom))) return CertificateValidity.NotYetValid;
  if (d.isAfter(dayjs(validUntil))) return CertificateValidity.Expired;

  return CertificateValidity.Valid;
}

/**
 * Ritorna le impostazioni per il tag in base allo stato di validità.
 */
function getTagOptions(validity: CertificateValidity, t: TFunction): TagProps {
  switch (validity) {
    case CertificateValidity.Valid:
      return {
        color: 'green',
        children: t('signer.certificato-in-validita', 'In validità'),
      };
    case CertificateValidity.Expired:
      return {
        color: 'red',
        children: t('signer.certificato-scaduto', 'Scaduto'),
      };
    case CertificateValidity.NotYetValid:
      return {
        color: 'orange',
        children: t(
          'signer.certificato-non-ancora-valido',
          'Non ancora valido',
        ),
      };
    case CertificateValidity.Unknown:
      return {
        children: t('signer.certificato-non-disponibile', 'N/D'),
      };
    default:
      checkNever(validity);
      return {
        children: t('signer.certificato-non-disponibile', 'N/D'),
      };
  }
}

export interface ICertificateValidityTagProps {
  signatureDate: string | null | undefined;
  validFrom: string | null | undefined;
  validUntil: string | null | undefined;
}

export function CertificateValidityTag(
  props: Readonly<ICertificateValidityTagProps>,
) {
  const { signatureDate, validFrom, validUntil } = props;
  const { t } = useTranslation();
  const validity = getValidity(signatureDate, validFrom, validUntil);

  return <StyledTagWithMargin {...getTagOptions(validity, t)} />;
}
